import React, { useState, useRef, useEffect } from 'react';
import { X } from 'lucide-react';

type FeedbackModalProps = {
  isVisible: boolean;
  feedbackType: 'positive' | 'negative';
  position: { x: number; y: number };
  onClose: () => void;
  onSubmit: (feedback: string | null, type: 'positive' | 'negative' | null) => void;
  initialComment?: string;
};

const FeedbackModal: React.FC<FeedbackModalProps> = ({
  isVisible,
  feedbackType,
  position,
  onClose,
  onSubmit,
  initialComment = '',
}) => {
  const [feedback, setFeedback] = useState(initialComment);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isVisible && textareaRef.current) {
      textareaRef.current.focus();
    }
  }, [isVisible]);

  useEffect(() => {
    setFeedback(initialComment);
  }, [initialComment, isVisible]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isVisible, onClose]);

  const handleSubmit = async (clearFeedback = false) => {
    if (clearFeedback || feedback.trim()) {
      setIsSubmitting(true);
      try {
        if (clearFeedback) {
          await onSubmit(null, null);
        } else {
          await onSubmit(feedback.trim(), feedbackType);
        }
        setFeedback('');
        onClose();
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClear = () => {
    handleSubmit(true);
  };

  const handleRegularSubmit = () => {
    handleSubmit(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleRegularSubmit();
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!isVisible) return null;

  const modalStyle: React.CSSProperties = {
    left: `${position.x}px`,
    top: `${position.y}px`,
  };

  return (
    <div
      ref={modalRef}
      style={modalStyle}
      className="fixed z-[1000] -translate-x-full translate-y-2 bg-BAWhite border border-BAMGray rounded-[10px] shadow-lg p-4 w-80 max-w-sm"
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-BABlack">
          {feedbackType === 'positive' ? 'What did you like?' : 'What could be improved?'}
        </h3>
        <button
          onClick={onClose}
          className="text-BAMGray hover:text-BABlack transition-colors"
          disabled={isSubmitting}
        >
          <X size={16} />
        </button>
      </div>
      
      <textarea
        ref={textareaRef}
        value={feedback}
        onChange={(e) => setFeedback(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder={
          feedbackType === 'positive'
            ? 'Tell us what worked well...'
            : 'Let us know what could be better...'
        }
        className="w-full h-24 p-2 border border-BALGray rounded-[6px] text-sm text-BABlack placeholder-BAMGray resize-none focus:outline-none focus:border-BAMBlue scrollbar-thin scrollbar-thumb-BAMGray scrollbar-track-BALGray"
        maxLength={2000}
        disabled={isSubmitting}
      />
      
      <div className="flex items-center justify-between mt-3">
        <span className="text-xs text-BAMGray">
          {feedback.length}/2000
        </span>
        <button
          onClick={handleClear}
          className="px-3 py-1.5 text-sm text-BAMGray hover:text-BABlack border border-BALGray rounded-full transition-colors"
          disabled={isSubmitting}
        >
          Clear
        </button>
        <div className="flex gap-2">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm text-BAMGray hover:text-BABlack border border-BALGray rounded-full transition-colors"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            onClick={handleRegularSubmit}
            disabled={!feedback.trim() || isSubmitting}
            className="px-3 py-1.5 text-sm text-BAMBlue bg-BAWhite border border-BAMBlue hover:bg-BALBlue disabled:bg-BALGray disabled:text-BAMGray disabled:border-BALGray disabled:cursor-not-allowed rounded-full transition-colors"
          >
            {isSubmitting ? 'Sending...' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;
